import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Stack,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuthContext } from '../../context/auth/AuthContext';
import { useUserContext } from '../../context/user_context/UserContext';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { useSubscriptionContext } from '../../context/subscription_context/SubscriptionContext';
import LoadingNavBar from '../../components/navbar/LoadingNavBar';
import Footer from '../../components/footer/Footer';
import Logo from '../../resources/images/logo/bizismart.png';
import UserProfileForm from './SignupFlow/forms/UserProfileForm';
import OrganizationForm from './SignupFlow/forms/OrganizationForm';
import { handleSignup } from '../../components/signup/SignupModule';
import SignupLoadingState from '../../components/loading/SignupLoadingState';
import { SignupState } from '../../utils/enums/SignupState';
import dayjs from 'dayjs';
import { UserStatus } from '../../components/routes/routing_statuses/RoutingStatuses';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../resources/strings/StringKeys';
const steps = [
  'Complete Profile',
  'Organization Details',
  'Payment Setup'
];

export default function SignupCompletionPage() {
  const { user } = useAuthContext();
  const { firebaseUser } = useUserContext();
  const { organization, markSubscriptionInitialized } = useOrganizationContext();
  const { subscription, hasActiveSubscription } = useSubscriptionContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [organizationId, setOrganizationId] = useState(() => {
    if (firebaseUser?.organizations?.[0]) return firebaseUser.organizations[0];
    if (organization) return organization.id;
    return null;
  });
  const [activeStep, setActiveStep] = useState(() => {
    if (firebaseUser === UserStatus.DOES_NOT_EXIST || organization === UserStatus.UNINITIALIZED) return 0;
    return 2;
  });
  const [signupData, setSignupData] = useState({
    userProfile: {},
    organizationDetails: {}
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (activeStep === 2) {
      setSignupData({
        userProfile: {},
        organizationDetails: {}
      });
    }
  }, [activeStep]);

  useEffect(() => {
    if (firebaseUser?.organizations?.[0])setOrganizationId(firebaseUser.organizations[0]);
    else if (organization && organization) setOrganizationId(organization.id);

    if (firebaseUser !== UserStatus.DOES_NOT_EXIST && organization !== UserStatus.UNINITIALIZED) setActiveStep(2);
  }, [firebaseUser, organization]);

  useEffect(() => {
    if (hasActiveSubscription && subscription && subscription.customer) markSubscriptionInitialized();
  }, [subscription, hasActiveSubscription, markSubscriptionInitialized, organizationId]);

  const handleStepSubmit = async (stepData) => {
    try {
      setError(null);
      setIsSubmitting(true);
      
      const updatedData = { ...signupData, ...stepData };
      
      if (stepData.userProfile) {
        if (stepData.userProfile?.birthday) updatedData.userProfile.birthday = stepData.userProfile.birthday.toISOString();
        if (stepData.userProfile?.hire_date) updatedData.userProfile.hire_date = stepData.userProfile.hire_date.toISOString();  
      }
      
      setSignupData(updatedData);

      if (activeStep === 0) {
        await handleSignup({ 
          ...updatedData, 
          step: 'profile',
          user
        });
        setActiveStep(1);
      } else if (activeStep === 1) {
        const result = await handleSignup({ 
          ...updatedData, 
          step: 'organization',
          user
        });
        if (result.organizationId) {
          setOrganizationId(result.organizationId);
        }
        setActiveStep(2);
      }
      
      setIsSubmitting(false);
    } catch (error) {
      setError(error.message);
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (isSubmitting) {
      return (
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <SignupLoadingState step={
            activeStep === 0 ? SignupState.NEEDS_PROFILE :
            activeStep === 1 ? SignupState.NEEDS_ORGANIZATION :
            SignupState.NEEDS_SUBSCRIPTION
          } />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <UserProfileForm
            initialData={{
              ...signupData.userProfile,
              birthday: signupData.userProfile?.birthday ? dayjs(signupData.userProfile.birthday) : undefined,
              hireDate: signupData.userProfile?.hire_date ? dayjs(signupData.userProfile.hire_date) : undefined,
            }}
            onSubmit={handleStepSubmit}
          />
        );
      case 1:
        return (
          <OrganizationForm
            initialData={signupData.organizationDetails}
            onSubmit={handleStepSubmit}
          />
        );
      case 2:
        if (!organizationId) {
          return (
            <Typography color="error" sx={{ mt: 2 }}>
              {t(StringKeys.ORGANIZATION_ID_MISSING)}
            </Typography>
          );
        }
        return (
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <Typography variant="h6" align="center" gutterBottom>
              {t(StringKeys.TRY_BIZISMART_FREE_FOR_30_DAYS)}
            </Typography>
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              '& stripe-pricing-table': {
                width: '100%',
                height: '100%'
              }
            }}>
              <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
              <stripe-pricing-table 
                pricing-table-id={process.env.REACT_APP_STRIPE_PRICING_TABLE_ID}
                publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
                client-reference-id={organizationId}
                customer-email={user.email}
              />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <LoadingNavBar showLoginButton={false} />
      <Box
        sx={{
          background: 'linear-gradient(135deg, #39ace7, #102a43)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 3, md: 5 }
        }}
      >
        <Container maxWidth="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              backgroundColor: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              display: 'flex',
              mt: 15,
              flexDirection: 'column',
              minHeight: { xs: '800px', md: '800px' },
              maxHeight: { md: '900px' },
              width: '100%'
            }}
          >
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <img 
                src={Logo} 
                alt="BiziSmart Logo" 
                style={{ 
                  height: '48px', 
                  marginBottom: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }} 
              />
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600, 
                  color: '#102a43', 
                  fontSize: { xs: '1.35rem', md: '1.5rem' },
                  letterSpacing: '-0.01em'
                }}
              >
                {t(StringKeys.COMPLETE_YOUR_ACCOUNT_SETUP)}
              </Typography>
            </Box>
            <Box sx={{mb:2, display: 'flex', justifyContent: 'center'}}>
              <Typography 
                variant="body" 
                sx={{
                  textAlign: 'center',
                }}
                gutterBottom>{t(StringKeys.MESSAGE_TO_OUR_USERS)}</Typography>
            </Box>

            {isMobile ? (
              // Simplified mobile stepper design - only circles with numbers/checkmarks
              <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                  {steps.map((label, index) => (
                    <Box 
                      key={label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: index === activeStep 
                            ? '#1976d2' 
                            : index < activeStep 
                              ? '#2e7d32' 
                              : '#e0e0e0',
                          color: index <= activeStep ? '#ffffff' : '#757575',
                          fontWeight: 'bold',
                          m: 1,
                          boxShadow: index <= activeStep ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        {index < activeStep ? '✓' : index + 1}
                      </Box>
                      {index < steps.length - 1 && (
                        <Box 
                          sx={{ 
                            height: 2, 
                            width: 20, 
                            backgroundColor: index < activeStep ? '#2e7d32' : '#e0e0e0',
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              // Desktop stepper remains the same
              <Stepper 
                activeStep={activeStep} 
                sx={{ 
                  mb: { xs: 3, md: 4 },
                  '& .MuiStepLabel-label': {
                    fontSize: { xs: '0.875rem', md: '0.925rem' },
                    fontWeight: 500
                  },
                  '& .MuiStepIcon-root': {
                    width: '28px',
                    height: '28px'
                  },
                  '& .MuiStepIcon-root.Mui-active': {
                    color: '#1976d2'
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: '#2e7d32'
                  }
                }}
              >
                { steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              mb: 2,
              '& form': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: 2
              },
              '& .MuiGrid-container': {
                width: '100%',
                margin: 0
              },
              '& stripe-pricing-table': {
                width: '100%',
                height: '100%',
                '--stripe-color-text-primary': '#102a43',
                '--stripe-color-text-secondary': '#486581',
                '--stripe-color-bg-primary': '#1976d2',
                '--stripe-color-bg-secondary': '#ffffff',
                '--stripe-color-border': '#e3e8ef'
              }
            }}>
              {renderStepContent()}
            </Box>
            {error && (
              <Typography 
                color="error" 
                sx={{ 
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  fontSize: '0.875rem'
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
} 